import Vue from 'vue'
import VueRouter from 'vue-router'
import VueI18n from 'vue-i18n'
import VueMeta from 'vue-meta'
import VueMoment from 'vue-moment'
import VueSnotify from 'vue-snotify'
import VueSweetalert2 from 'vue-sweetalert2'
import VueTheMask from 'vue-the-mask'
import VueMoney from 'v-money'
import TransitionExpand from 'vue-transition-expand'
import VeeValidate from 'vee-validate'
import VueCookies from 'vue-cookies'
import VueGtag from 'vue-gtag'
import PortalVue from 'portal-vue'

// @ts-ignore
import {VTooltip, VPopover, VClosePopover} from 'v-tooltip'
import {merge} from 'lodash'

import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import GridLoader from 'vue-spinner/src/GridLoader.vue'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'
import RiseLoader from 'vue-spinner/src/RiseLoader.vue'
import BeatLoader from 'vue-spinner/src/BeatLoader.vue'
import SyncLoader from 'vue-spinner/src/SyncLoader.vue'
import RotateLoader from 'vue-spinner/src/RotateLoader.vue'
import FadeLoader from 'vue-spinner/src/FadeLoader.vue'
import PacmanLoader from 'vue-spinner/src/PacmanLoader.vue'
import SquareLoader from 'vue-spinner/src/SquareLoader.vue'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import SkewLoader from 'vue-spinner/src/SkewLoader.vue'
import MoonLoader from 'vue-spinner/src/MoonLoader.vue'
import RingLoader from 'vue-spinner/src/RingLoader.vue'
import BounceLoader from 'vue-spinner/src/BounceLoader.vue'
import DotLoader from 'vue-spinner/src/DotLoader.vue'
import {$} from '@/facade'
import {Lang} from '@/enums/Lang'
import {Currency} from '@/enums/Currency'
import VueAdapTable from '@simpli/vue-adap-table'
import VueAwait from '@simpli/vue-await'
import VueModal from '@simpli/vue-modal'
import VueRenderSchema from '@simpli/vue-render-schema'
import {PageCollection} from '@simpli/resource-collection'
import {RequestConfig, RequestListener} from '@simpli/serialized-request'
import * as validators from '@brazilian-utils/brazilian-utils'
import {SwiperGlobalOptions} from '@/app/SwiperGlobalOptions'
import moment from 'moment-timezone'
import momentDurationFormatSetup from 'moment-duration-format'

import {
  Swiper as SwiperClass,
  Pagination,
  Mousewheel,
  Autoplay,
  // @ts-ignore
} from 'swiper/swiper.esm'
import getAwesomeSwiper from 'vue-awesome-swiper/dist/exporter'
import Rollbar from 'rollbar'
import {EnvHelper} from '@/helpers/EnvHelper'
import {version} from '../../package.json'

export class Setup {
  static init() {
    Vue.prototype.$rollbar = new Rollbar({
      accessToken: EnvHelper.VUE_APP_ROLLBAR_ACCESS_TOKEN,
      captureUncaught: true,
      captureUnhandledRejections: true,
      captureIp: false,
      payload: {
        // Track your events to a specific version of code for better visibility into version health
        code_version: version,
        environment: EnvHelper.VUE_APP_ENV,
      },
      enabled: EnvHelper.NODE_ENV === 'production',
      transmit: EnvHelper.NODE_ENV === 'production',
    })

    Vue.config.productionTip = false

    Vue.prototype.$config = $.config
    Vue.prototype.$app = $.app
    Vue.prototype.$auth = $.auth
    Vue.prototype.$walletAdapter = $.walletAdapter
    Vue.prototype.$translate = function(
      this: Vue,
      key: string,
      values?: any
    ): string {
      const translate = this.$t ? this.$t.bind(this) : $.vm.$t.bind($.vm)
      return translate(key, values) as string
    }
    Vue.prototype.$env = $.env
    Vue.prototype.$file = $.file
    Vue.prototype.$xlsx = $.xlsx
    Vue.prototype.$dialog = $.dialog
    Vue.prototype.$file = $.file
    Vue.prototype.$filter = $.filter
    Vue.prototype.$nav = $.nav
    Vue.prototype.$toast = $.toast
    Vue.prototype.$utils = $.utils

    const swiperOptions = new SwiperGlobalOptions()

    SwiperClass.use([Pagination, Mousewheel, Autoplay])

    Vue.use(VueAdapTable, {
      defaultBeforeQueryAction: () => $.await.init('softQuery'),
      defaultAfterQueryAction: () => $.await.done('softQuery'),
      defaultErrorQueryAction: () => $.await.error('softQuery'),
      swiperOptions,
      screenBounds: {
        small: $.env.SMALL_SCREEN,
        medium: $.env.MEDIUM_SCREEN,
        large: $.env.LARGE_SCREEN,
      },
    })

    Vue.config.errorHandler = (err, vm) => {
      vm.$rollbar.error(err)
      throw err // rethrow
    }

    Vue.use(PortalVue)
    Vue.use(VueGtag, {config: {id: 'G-PZHKFDQ8HH'}, bootstrap: false})
    Vue.use(VueCookies)
    Vue.use(VueAwait)
    Vue.use(getAwesomeSwiper(SwiperClass), swiperOptions)
    Vue.use(VueModal)
    Vue.use(VueRenderSchema)
    Vue.use(VueI18n)
    Vue.use(VueMeta)
    Vue.use(VueMoment)
    Vue.use(VueRouter)
    Vue.use(VueSnotify)
    Vue.use(VueSweetalert2)
    Vue.use(VueTheMask)
    Vue.use(TransitionExpand)
    Vue.use(VeeValidate, {
      i18n: new VueI18n($.config.i18n),
      useConstraintAttrs: false,
      dictionary: merge(
        $.config.i18n.messagesVeeValidate,
        $.config.i18n.messages
      ),
    })

    Vue.directive('tooltip', VTooltip)
    Vue.directive('close-popover', VClosePopover)
    Vue.component('v-popover', VPopover)

    const filter: any = $.filter
    for (const key in filter) {
      if (filter.hasOwnProperty(key)) {
        Vue.filter(key, filter[key])
      }
    }

    moment.locale($.config.i18n.locale)
    moment.tz.setDefault('UTC')
    // @ts-ignore
    momentDurationFormatSetup(moment)

    RequestConfig.axios = $.config.http.axiosInstance
    RequestListener.onRequestStart(reqName => $.await.init(reqName))
    RequestListener.onRequestEnd(reqName => $.await.done(reqName))
    RequestListener.onRequestError(reqName => $.await.error(reqName))

    PageCollection.defaultMinCharToSearch = 1
    PageCollection.defaultCurrentPage = 0
    PageCollection.defaultPerPage = 10

    VeeValidate.Validator.extend('cpf', {
      validate: (value?: string) => validators.isValidCPF(value ?? ''),
    })
    VeeValidate.Validator.extend('cnpj', {
      validate: (value?: string) => validators.isValidCNPJ(value ?? ''),
    })
    VeeValidate.Validator.extend('phone', {
      validate: (value?: string) => validators.isValidPhone(value ?? ''),
    })
    VeeValidate.Validator.extend('cep', {
      validate: (value?: string) => validators.isValidCEP(value ?? ''),
    })
    VeeValidate.Validator.extend('boleto', {
      validate: (value?: string) => validators.isValidBoleto(value ?? ''),
    })

    $.await.addLoader('PulseLoader', PulseLoader)
    $.await.addLoader('GridLoader', GridLoader)
    $.await.addLoader('ClipLoader', ClipLoader)
    $.await.addLoader('RiseLoader', RiseLoader)
    $.await.addLoader('BeatLoader', BeatLoader)
    $.await.addLoader('SyncLoader', SyncLoader)
    $.await.addLoader('RotateLoader', RotateLoader)
    $.await.addLoader('FadeLoader', FadeLoader)
    $.await.addLoader('PacmanLoader', PacmanLoader)
    $.await.addLoader('SquareLoader', SquareLoader)
    $.await.addLoader('ScaleLoader', ScaleLoader)
    $.await.addLoader('SkewLoader', SkewLoader)
    $.await.addLoader('MoonLoader', MoonLoader)
    $.await.addLoader('RingLoader', RingLoader)
    $.await.addLoader('BounceLoader', BounceLoader)
    $.await.addLoader('DotLoader', DotLoader)

    $.await.defaultTransition = 'fade'
    $.await.defaultSpinner = 'BounceLoader'
    $.await.defaultSpinnerColor = $.env.OCEAN_GREEN_COLOR
    $.await.defaultSpinnerPadding = '0'
    $.await.defaultSpinnerScale = 1

    $.modal.defaultBody = document.body
    $.modal.defaultTransition = 'blur'
    $.modal.defaultBackgroundTransition = 'fade'
    $.modal.defaultClosable = true
    $.modal.defaultCloseOutside = true

    $.snotify.setDefaults({
      global: $.config.toast.global,
      toast: $.config.toast.default,
    })

    $.app.language = $.config.i18n.locale
    $.app.currency = $.config.i18n.currency

    // Auth guard: routes flagged with meta.requiresAuth (the whole /dashboard area)
    // require a logged-in session; otherwise redirect to the sign-in page.
    const isAuthBypassed = EnvHelper.VUE_APP_DASHBOARD_AUTH_BYPASS

    $.router.beforeEach((to, from, next) => {
      const requiresAuth = to.matched.some(
        record => record.meta && record.meta.requiresAuth
      )
      if (requiresAuth && !isAuthBypassed && !$.auth.isLogged) {
        next({name: 'signIn'})
      } else {
        next()
      }
    })

    // The router kicks off its initial navigation when the Vue instance is created
    // (facade.ts), before the guard above is registered — so beforeEach only covers
    // later navigations. Re-check the initial route (direct URL / refresh) once ready.
    $.router.onReady(() => {
      const requiresAuth = $.router.currentRoute.matched.some(
        record => record.meta && record.meta.requiresAuth
      )
      if (requiresAuth && !isAuthBypassed && !$.auth.isLogged) {
        $.router.replace({name: 'signIn'}).catch(() => undefined)
      }
    })

    // A persisted token keeps the session across refresh; load the user from it and
    // validate it (an invalid/expired token 401s and the interceptor signs out).
    if ($.auth.token) {
      $.auth.authenticate()
    }

    $.vm.$mount('#app')

    $.vm.$nextTick(() => {
      const style = document.createElement('style')
      style.innerHTML = `
        html, body {
          overflow: auto !important;
          height: auto !important;
          position: static !important;
        }
      `
      document.head.appendChild(style)
    })

    // @ts-ignore
    window.appLoader?.finish()
  }

  static changeLocale(lang: Lang) {
    $.vm.$i18n.locale = lang
  }

  static changeCurrency(currency: Currency) {
    Vue.use(VueMoney, {
      decimal: $.translate('lang.decimal'),
      thousands: $.translate('lang.thousands'),
      prefix: $.translate(`currency.${currency}.prefix`),
      precision: Number($.translate(`currency.${currency}.precision`)),
    })
  }
}
